export const GNSS_DISPLAY_DEFAULTS = Object.freeze({
  primary: true, secondary: true, nominal: true, references: true,
  fixTolerance: false, waypointProjection: false, dfDiagnostics: false, tfDiagnostics: false,
  excludedSpiralDiagnostics: false, conservativeDfDiagnostics: false,
});

const CATEGORY_COLORS = { A: '#1565c0', B: '#00796b', C: '#7b1fa2', D: '#c62828', DL: '#b36b00', E: '#5d4037', H: '#2e7d32' };
export const gnssCategory = proc => String(proc.input?.aircraft_category || 'Unknown');
export const gnssCategoryColor = proc => CATEGORY_COLORS[gnssCategory(proc)] || '#455a64';
export const gnssProcedureKey = proc => JSON.stringify([gnssCategory(proc), proc.input?.id ?? proc.input?.approach_id,
  proc.input?.airport_code, proc.input?.runway_designator, proc.input?.approach_type, proc.input?.xls_oas_category]);

export function gnssExcludedSpiralCount(proc) {
  const construction = proc.constructionDiagnostics;
  return construction ? (construction.legs || [construction]).reduce((count, leg) => count
    + (leg.paths || []).filter(path => path.kind === 'wind_spiral' && path.role === 'excluded_non_controlling').length, 0) : 0;
}

export function gnssExtendedSpiralCount(proc) {
  const construction = proc.constructionDiagnostics;
  return construction ? (construction.legs || [construction])
    .filter(leg => Number(leg.spiralQuarterCount) > 3).length : 0;
}

export function gnssConservativeDfCount(proc) {
  const construction = proc.constructionDiagnostics;
  return construction ? (construction.legs || [construction]).filter(leg => leg.conservativeReview || leg.endJoinReview).length : 0;
}

export function gnssDfEndJoinCount(proc) {
  const construction = proc.constructionDiagnostics;
  return construction ? (construction.legs || [construction]).filter(leg => leg.endJoinReview).length : 0;
}

export function gnssSegmentGroup(seg) {
  const family = String(seg.segFamily || seg.segType || '').toLowerCase();
  if (family.startsWith('initial')) return 'initial';
  if (family.startsWith('intermediate')) return 'intermediate';
  if (family.startsWith('missed') || family === 'ma_main') return 'missed';
  return 'final';
}

export function gnssSegmentKey(seg) {
  const group = gnssSegmentGroup(seg);
  const surface = seg.baroSurface || seg.lpvSurface || seg.xlsSurface;
  if (surface) return `${group}:${seg.baroSurface ? 'BARO' : seg.lpvSurface ? 'LPV' : 'xLS'}:${surface}`;
  const index = String(seg.segLabel || '').match(/^(?:initial|intermediate)\s+(\d+)/i)?.[1];
  return `${group}:${index || seg.segLabel || seg.segType || 'area'}`;
}

export function isGnssProcedureVisible(state, proc) {
  return state?.categories?.[gnssCategory(proc)] !== false
    && state?.procedures?.[gnssProcedureKey(proc)]?.enabled !== false;
}

export function isGnssDisplayEnabled(state, proc, option) {
  if (!isGnssProcedureVisible(state, proc)) return false;
  return (state?.procedures?.[gnssProcedureKey(proc)]?.options?.[option]
    ?? state?.categoryOptions?.[gnssCategory(proc)]?.[option]
    ?? state?.[option] ?? GNSS_DISPLAY_DEFAULTS[option]) === true;
}

export function isGnssSegmentVisible(state, proc, seg) {
  if (!isGnssProcedureVisible(state, proc)) return false;
  const config = state?.procedures?.[gnssProcedureKey(proc)];
  return config?.groups?.[gnssSegmentGroup(seg)] !== false
    && config?.segments?.[gnssSegmentKey(seg)] !== false;
}

export function groupGnssLayers(results) {
  const categories = new Map();
  for (const proc of results || []) {
    const category = gnssCategory(proc);
    if (!categories.has(category)) categories.set(category, []);
    categories.get(category).push(proc);
  }
  const order = ['A', 'B', 'C', 'D', 'DL', 'E', 'H'];
  return [...categories].sort(([a], [b]) => (order.indexOf(a) < 0 ? 99 : order.indexOf(a))
    - (order.indexOf(b) < 0 ? 99 : order.indexOf(b)));
}
